import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * The batch, as one line — segmented by the containers' own statuses.
 *
 * These are exactly the values the Status column shows, in lifecycle order, so
 * the bar fills left to right as the batch progresses and a count in the legend
 * can be matched against the rows below it.
 *
 * The five middle segments were one `in_progress` band. Splitting them is what
 * makes the bar worth reading on a batch of thirty: "twenty in progress" said
 * nothing about whether the day's work was at the gate or nearly done, and the
 * shades deepen through the run so that shape is visible without counting.
 *
 * `cancelled` sits outside the lifecycle rather than at the end of it: a
 * cancelled container is not progress, and including it in the fill would make
 * an order look further along the more of it was called off.
 *
 * `at_stop` and `stop_completed` are the two the ordering cannot place. A route
 * may put an added stop anywhere — a weighbridge before the client, a customs
 * office after — so there is no point in the sequence they belong at and no
 * shade of the run's own hue they can honestly take. They sit between the
 * collection and the loading site, which is where most of them fall, in amber:
 * off the scale on purpose, because the bar's shading is a claim about how far
 * along a container is and these two are the only statuses that cannot make it.
 */
const STATUS_SEGMENTS: Array<{
  status: string;
  barClass: string;
  dotClass: string;
}> = [
  // Before the run rather than at the start of it: a container the office has
  // not released is not zero progress along a journey, it is a box that has not
  // been sent on one. Palest on the bar for the same reason.
  { status: "created", barClass: "bg-secondary-100", dotClass: "bg-secondary-100" },
  { status: "planned", barClass: "bg-secondary-300", dotClass: "bg-secondary-300" },
  { status: "at_pickup_terminal", barClass: "bg-blue-200", dotClass: "bg-blue-200" },
  { status: "picked_up", barClass: "bg-blue-400", dotClass: "bg-blue-400" },
  { status: "at_stop", barClass: "bg-amber-300", dotClass: "bg-amber-300" },
  { status: "stop_completed", barClass: "bg-amber-500", dotClass: "bg-amber-500" },
  { status: "at_loading_site", barClass: "bg-sky-500", dotClass: "bg-sky-500" },
  { status: "loaded", barClass: "bg-indigo-500", dotClass: "bg-indigo-500" },
  { status: "at_dropoff_terminal", barClass: "bg-violet-500", dotClass: "bg-violet-500" },
  { status: "returned", barClass: "bg-violet-700", dotClass: "bg-violet-700" },
  { status: "completed", barClass: "bg-success", dotClass: "bg-success" },
];

interface LineProgressBarProps {
  /** Line count per status, for the whole batch — not just the loaded page. */
  byStatus: Record<string, number>;
  /**
   * Batch size. Segment widths are computed against the *summed* statuses
   * rather than this, so a mismatch between the two can never produce a bar
   * that over- or under-fills; this is only the fallback when nothing is known.
   */
  total: number;
  /** Cancelled lines, shown as a separate chip rather than a bar segment. */
  cancelled?: number;
  className?: string;
}

export function LineProgressBar({
  byStatus,
  total,
  cancelled,
  className = "",
}: LineProgressBarProps) {
  const { t } = useTranslation("containerOrders");

  const segments = useMemo(
    () =>
      STATUS_SEGMENTS.map((segment) => ({
        ...segment,
        label: t(`linesBlock.statuses.${segment.status}`, {
          defaultValue: segment.status,
        }),
        count: byStatus?.[segment.status] ?? 0,
      })),
    [byStatus, t],
  );

  // Widths are a share of what the statuses actually add up to. Using the
  // `total` prop here instead would leave a phantom gap whenever the two
  // disagree — which is precisely the symptom of a miscounted batch, and the
  // bar should not be the thing that hides it.
  const counted = segments.reduce((sum, segment) => sum + segment.count, 0);
  const denominator = counted || total;

  if (!denominator) return null;

  // Only statuses the batch contains get a slice; a zero-width sliver is noise.
  //
  // The legend shows the same ones, which it did not use to: with three statuses
  // listing all of them was a key, and an unused colour was the one most needing
  // an explanation. With seven it is a wall of mostly-zero chips in front of the
  // handful that are true, and a status no container holds is not a colour
  // anybody needs explained — the bar has no band in it to ask about.
  const filled = segments.filter((segment) => segment.count > 0);

  return (
    <div className={`space-y-2 ${className}`}>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary-100"
        role="img"
        aria-label={filled
          .map((segment) => `${segment.count} ${segment.label}`)
          .join(", ")}
      >
        {filled.map((segment) => (
          <div
            key={segment.status}
            className={segment.barClass}
            style={{ width: `${(segment.count / denominator) * 100}%` }}
            title={`${segment.count} ${segment.label}`}
          />
        ))}
      </div>

      {/* Legend — one chip per status, carrying the exact swatch used in the
          bar above it, so the mapping reads without a separate key. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {filled.map((segment) => (
          <span
            key={segment.status}
            className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(11,15,14,0.12)] bg-white px-2 py-0.5 text-[11px] leading-5 text-secondary-700"
          >
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${segment.dotClass}`}
              aria-hidden
            />
            <span className="font-bold text-ink">{segment.count}</span>
            <span className="whitespace-nowrap">{segment.label}</span>
          </span>
        ))}

        {!!cancelled && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(11,15,14,0.12)] bg-transparent px-2 py-0.5 text-[11px] font-medium leading-5 text-secondary-500">
            {t("linesBlock.cancelledCount", { count: cancelled })}
          </span>
        )}
      </div>
    </div>
  );
}
